import { CreditCardOutlined } from "@ant-design/icons";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Alert, Button } from "antd";
import React, { useState } from "react";

interface PaymentFormProps {
	onPaymentMethodCreated: (paymentMethodId: string) => void;
	onError: (error: string) => void;
	loading?: boolean;
	buttonText?: string;
}

const cardElementOptions = {
	style: {
		base: {
			fontSize: "16px",
			color: "#424770",
			"::placeholder": {
				color: "#aab7c4",
			},
		},
		invalid: {
			color: "#9e2146",
		},
	},
};

export const PaymentForm: React.FC<PaymentFormProps> = ({
	onPaymentMethodCreated,
	onError,
	loading = false,
	buttonText = "Add Payment Method",
}) => {
	const stripe = useStripe();
	const elements = useElements();
	const [processing, setProcessing] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();

		if (!stripe || !elements) {
			return;
		}

		setProcessing(true);
		setError(null);

		const cardElement = elements.getElement(CardElement);

		if (!cardElement) {
			setError("Card element not found");
			setProcessing(false);
			return;
		}

		try {
			const { error, paymentMethod } = await stripe.createPaymentMethod({
				type: "card",
				card: cardElement,
			});

			if (error) {
				setError(error.message || "An error occurred");
				onError(error.message || "An error occurred");
			} else if (paymentMethod) {
				onPaymentMethodCreated(paymentMethod.id);
			}
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "An unexpected error occurred";
			setError(errorMessage);
			onError(errorMessage);
		} finally {
			setProcessing(false);
		}
	};

	return (
		<form onSubmit={handleSubmit} style={{ width: "100%" }}>
			<div
				style={{
					padding: "16px",
					border: "1px solid #d9d9d9",
					borderRadius: "6px",
					marginBottom: "16px",
					backgroundColor: "#fafafa",
				}}
			>
				<CardElement options={cardElementOptions} />
			</div>

			{error && (
				<Alert
					message="Payment Error"
					description={error}
					type="error"
					style={{ marginBottom: "16px" }}
					showIcon
				/>
			)}

			<Button
				type="primary"
				htmlType="submit"
				loading={processing || loading}
				disabled={!stripe || processing || loading}
				icon={<CreditCardOutlined />}
				size="large"
				block
			>
				{processing ? "Processing..." : buttonText}
			</Button>
		</form>
	);
};
